import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { Booking } from '../../../../models/booking.model';
import { getFormattedDate } from 'utils/helper.utils';

// Define PDF styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        padding: 30,
        fontFamily: 'Helvetica',
    },
    title: {
        fontSize: 18,
        marginBottom: 10,
        textAlign: 'center'
    },
    subtitle: {
        fontSize: 14,
        marginBottom: 10
    },
    table: {
        display: 'flex',
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0
    },
    tableRow: {
        flexDirection: 'row',
    },
    tableRowHeader: {
        flexDirection: 'row',
        backgroundColor: '#f0f0f0',
        fontWeight: 'bold'
    },
    tableCol: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        flex: 1
    },
    tableColId: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        width: '16%'
    },
    tableColCustomer: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        width: '15%'
    },
    tableColEmail: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        width: '22%'
    },
    tableColDate: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        width: '12%'
    },
    tableColSeats: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        width: '8%'
    },
    tableColPrice: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        width: '9%'
    },
    tableColStatus: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        width: '10%'
    },
    tableColCheckIn: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 5,
        width: '12%'
    },
    tableCell: {
        fontSize: 8,
        textAlign: 'left'
    },
    tableCellHeader: {
        fontSize: 9,
        textAlign: 'left',
        fontWeight: 'bold'
    },
    info: {
        marginBottom: 20
    },
    infoItem: {
        fontSize: 10,
        marginBottom: 5
    }
});


// Define BookingsPDF component
export const BookingsPDF = ({ bookings, filters }: { bookings: Booking[], filters: any }) => {
    // Split bookings into chunks of 22 items per page
    const ITEMS_PER_PAGE = 18;
    const bookingChunks = [];

    for (let i = 0; i < bookings.length; i += ITEMS_PER_PAGE) {
        bookingChunks.push(bookings.slice(i, i + ITEMS_PER_PAGE));
    }

    const isDayDateFilterApplied = filters.date && filters.dateRange === 'day';
    const isCustomDateFilterApplied = filters.dateRange === 'custom';

    return (
        <Document>
            {
                bookingChunks.map((chunk, pageIndex) => (
                    <Page key={pageIndex} size="A4" orientation="landscape" style={styles.page} >
                        <Text style={styles.title} > Bookings Report {bookingChunks.length > 1 ? `- Page ${pageIndex + 1}/${bookingChunks.length}` : ''} </Text>

                        < View style={styles.info} >
                            <Text style={styles.infoItem} > Generated: {getFormattedDate(null, 'yyyy-MM-dd HH:mm:ss')} </Text>
                            {filters.search && <Text style={styles.infoItem} > Search: {filters.search} </Text>}
                            {isCustomDateFilterApplied && <Text style={styles.infoItem}> Date Range {filters.dateRange.charAt(0).toUpperCase() + filters.dateRange.slice(1)}: {getFormattedDate(filters.startDate)} - {getFormattedDate(filters.endDate)} </Text>}
                            {isDayDateFilterApplied && <Text style={styles.infoItem}> Event Date: {getFormattedDate(filters.date)} </Text>}
                            <Text style={styles.infoItem}> Total Bookings: {bookings.length} </Text>
                        </View>

                        < View style={styles.table} >
                            <View style={styles.tableRowHeader}>
                                <View style={styles.tableColId}> <Text style={styles.tableCellHeader}> ID </Text></View >
                                <View style={styles.tableColCustomer}> <Text style={styles.tableCellHeader}> Customer </Text></View >
                                <View style={styles.tableColEmail}> <Text style={styles.tableCellHeader}> Email </Text></View >
                                <View style={styles.tableColDate}> <Text style={styles.tableCellHeader}> Date </Text></View >
                                <View style={styles.tableColSeats}> <Text style={styles.tableCellHeader}> Seats </Text></View >
                                <View style={styles.tableColPrice}> <Text style={styles.tableCellHeader}> Price </Text></View >
                                <View style={styles.tableColStatus}> <Text style={styles.tableCellHeader}> Status </Text></View >
                                <View style={styles.tableColCheckIn}> <Text style={styles.tableCellHeader}> Check In </Text></View >
                            </View>

                            {
                                chunk.map((booking) => (
                                    <View style={styles.tableRow} key={booking._id} >
                                        <View style={styles.tableColId} > <Text style={styles.tableCell} > {booking.short_id} </Text></View >
                                        <View style={styles.tableColCustomer} > <Text style={styles.tableCell} > {booking.customer_name} </Text></View >
                                        <View style={styles.tableColEmail} > <Text style={styles.tableCell} > {booking.customer_email} </Text></View >
                                        <View style={styles.tableColDate} >
                                            <Text style={styles.tableCell} > {new Date(booking.booking_date).toLocaleDateString()} </Text>
                                        </View>
                                        < View style={styles.tableColSeats} > <Text style={styles.tableCell} > {booking.booked_total_seats} </Text></View >
                                        <View style={styles.tableColPrice} > <Text style={styles.tableCell} > {booking.total_price} </Text></View >
                                        <View style={styles.tableColStatus} > <Text style={styles.tableCell} > {booking.payment_status?.toUpperCase()} </Text></View >
                                        <View style={styles.tableColCheckIn} >
                                            <Text style={styles.tableCell} > {booking.is_checked_in ? 'Checked In' : 'Not Checked In'} </Text>
                                        </View>
                                    </View>
                                ))
                            }
                        </View>

                        < Text style={{
                            position: 'absolute',
                            bottom: 30,
                            left: 0,
                            right: 0,
                            textAlign: 'center',
                            fontSize: 8,
                            color: 'grey'
                        }
                        }>
                            Page {pageIndex + 1} of {bookingChunks.length}
                        </Text>
                    </Page>
                ))}
        </Document>
    );
};